import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/user';
import { useAirtableStore } from '@/stores/airtable';
import { checkRegistrationOpen } from '~/utilities/validation.utils';

import RemoveSeatModal from '~/components/fragments/sessions/modals/remove-seat.vue';
import SeatReservationNotOpenedModal from '~/components/fragments/sessions/modals/seat-reservation-not-opened.vue';
import SeatSavedModal from '~/components/fragments/sessions/modals/seat-saved.vue';
import DoubleBookedModal from '~/components/fragments/sessions/modals/double-booked.vue';
import SessionAtCapacityModal from '~/components/fragments/sessions/modals/session-at-capacity.vue';
import FavoritesSavedModal from '~/components/fragments/sessions/modals/favorites-saved.vue';
import FavoritesSavedReservationRequiredModal from '~/components/fragments/sessions/modals/favorites-saved-reservation-required.vue';
import RemoveSeatRemoveFavoritesModal from '~/components/fragments/sessions/modals/remove-seat-remove-favorites.vue';
import ConfirmRemoveFavoriteModal from '~/components/fragments/sessions/modals/confirm-remove-favorite.vue';
import RemoveFavoritesRemoveSeatModal from '~/components/fragments/sessions/modals/remove-favorites-remove-seat.vue';
import FavoritesSavedReservationNotOpened from '~/components/fragments/sessions/modals/favorites-saved-reservation-not-opened.vue';
import FavoritesSavedReservationNotEligible from '~/components/fragments/sessions/modals/favorites-saved-reservation-not-eligible.vue';
import HubspotterErrorModal from '~/components/fragments/sessions/modals/hubspotter-error-modal.vue';

export default function useSessionActions(session) {
    const userStore = useUserStore();
    const airtableStore = useAirtableStore();

    airtableStore.fetchSessions();
    const { sessions } = storeToRefs(airtableStore);

    const doubleBookedSession = ref(null);
    const { $bus } = useNuxtApp();
    const { user } = storeToRefs(userStore);

    const isFavoriteLoading = ref(false);
    const isSaveSeatLoading = ref(false);

    const activeModal = ref(null);
    const modalOpen = ref(false);

    const isFavorite = computed(() => {
        return (
            user?.value?.favorites?.includes(session.unique_id) ||
            user?.value?.favorites?.includes(parseInt(session.unique_id))
        );
    });

    const isReserved = computed(() => {
        return (
            user?.value?.sessionRegistration?.includes(session.unique_id) ||
            user?.value?.sessionRegistration?.includes(parseInt(session.unique_id))
        );
    });

    const toggleFavorite = async (localRef = false) => {
        if (!userStore.isAuthenticated) {
            $bus.emit('toggle-login-modal');
            return;
        }

        try {
            isFavoriteLoading.value = true;

            if (isFavorite.value) {
                showRemoveFavoritesModal();
                // await userStore.unfavoriteSession(session.unique_id);
                // showRemoveFavoritesRemoveSeatModal();
            } else {
                await userStore.favoriteSession(session.unique_id);

                if (session.session_reservations && !isReserved.value) {
                    try {
                        checkRegistrationOpen(userStore.user);
                        activeModal.value = 'favoritesSavedReservationRequired';
                    } catch (error) {
                        if (error.message.includes('Your ticket type is not eligible for seat reservations.')) {
                            activeModal.value = 'favoritesSavedReservationNotEligible';
                        } else {
                            activeModal.value = 'favoritesSavedReservationNotOpened';
                        }
                    }
                    modalOpen.value = true;
                } else {
                    activeModal.value = 'favoritesSaved';
                }
                modalOpen.value = true;
                if (localRef) localRef.value = false;
            }
        } catch (error) {
            console.error('Error updating favorite status for session:', error);
        } finally {
            isFavoriteLoading.value = false;
            if (localRef) localRef.value = false;
        }
    };

    const handleSaveSeat = async (componentRef = false, isSaveSeatLoading, holdOpen) => {
        try {
            isSaveSeatLoading.value = true;
            if (isReserved.value) {
                activeModal.value = 'removeSeat';
                modalOpen.value = true;
            } else {
                if (holdOpen) {
                    //  console.log('in here');
                    modalOpen.value = true;
                    activeModal.value = holdOpen.activeModal;
                }
                const { data, pending, error, refresh } = await useAsyncData(
                    'reserveSeat',
                    async () => await reserveSeat()
                );
            }
        } catch (error) {
            if (holdOpen) {
                activeModal.value = '';
            }
            console.error('Error updating seat reservation for session:', error);
        } finally {
            isSaveSeatLoading.value = false;
            if (componentRef) componentRef.value = false;
        }
    };

    const findDoubleBookedSession = () => {
        const currentStart = new Date(session.start_date_and_time);
        const currentEnd = new Date(session.end_date_and_time);

        for (const sessionId of userStore.user.sessionRegistration) {
            const reservedSession = sessions.value.find((s) => s.unique_id === sessionId);
            if (!reservedSession) continue;

            const reservedStart = new Date(reservedSession.start_date_and_time);
            const reservedEnd = new Date(reservedSession.end_date_and_time);

            if (sessionsOverlap(currentStart, currentEnd, reservedStart, reservedEnd)) {
                doubleBookedSession.value = reservedSession;
                return;
            }
        }
    };

    const sessionsOverlap = (start1, end1, start2, end2) => {
        return start1 < end2 && start2 < end1;
    };

    const reserveSeat = async () => {
        if (!userStore.isAuthenticated) {
            $bus.emit('toggle-login-modal');
            return;
        }
        try {
            isSaveSeatLoading.value = true;
            await userStore.registerTicket(session.unique_id);

            if (userStore.user.sessionRegistration) {
                userStore.user.sessionRegistration.push(session.unique_id);
            } else {
                userStore.user.sessionRegistration = [session.unique_id];
            }

            // Automatically favorite the session
            if (!isFavorite.value) {
                await userStore.favoriteSession(session.unique_id);
            }

            activeModal.value = 'seatSaved';
            modalOpen.value = true;
        } catch (error) {
            if (error.data.message.includes('Seat reservations will open on')) {
                activeModal.value = 'seatReservationNotOpened';
                modalOpen.value = true;
            } else if (error.data.message.includes('double book')) {
                findDoubleBookedSession();
                activeModal.value = 'doubleBooked';
                modalOpen.value = true;
            } else if (error.data?.data?.message.includes('Session capacity reached')) {
                activeModal.value = 'sessionAtCapacity';
                modalOpen.value = true;
            } else if (error.data?.message.includes('Your ticket type is not eligible for seat reservations.')) {
                (activeModal.value = 'hubspotterErrorModal'), (modalOpen.value = true);
            } else {
                throw error;
            }
        } finally {
            isSaveSeatLoading.value = false;
        }
    };

    const unreserveSeat = async () => {
        try {
            isSaveSeatLoading.value = true;
            await userStore.unregisterTicket(parseInt(session.unique_id));
            const index = user.value.sessionRegistration.indexOf(session.unique_id);
            if (index !== -1) {
                user.value.sessionRegistration.splice(index, 1);
            }
        } catch (error) {
            console.error('Error updating seat reservation for session:', error);
        } finally {
            isSaveSeatLoading.value = false;
            resetModal();
        }
    };

    const resetModal = () => {
        activeModal.value = null;
        modalOpen.value = false;
    };

    const modalComponents = {
        removeSeat: RemoveSeatModal,
        seatReservationNotOpened: SeatReservationNotOpenedModal,
        seatSaved: SeatSavedModal,
        doubleBooked: DoubleBookedModal,
        sessionAtCapacity: SessionAtCapacityModal,
        favoritesSaved: FavoritesSavedModal,
        favoritesSavedReservationRequired: FavoritesSavedReservationRequiredModal,
        removeSeatRemoveFavorites: RemoveSeatRemoveFavoritesModal,
        confirmRemoveFavorite: ConfirmRemoveFavoriteModal,
        removeFavoritesRemoveSeat: RemoveFavoritesRemoveSeatModal,
        favoritesSavedReservationNotOpened: FavoritesSavedReservationNotOpened,
        favoritesSavedReservationNotEligible: FavoritesSavedReservationNotEligible,
        hubspotterErrorModal: HubspotterErrorModal,
    };

    const currentModalComponent = computed(() => modalComponents[activeModal.value]);

    const showRemoveFavoriteModal = () => {
        resetModal();
        if (isFavorite.value) {
            activeModal.value = 'removeSeatRemoveFavorites';
            modalOpen.value = true;
        } else {
            resetModal();
        }
    };

    const showConfirmRemoveFavoritesModal = () => {
        activeModal.value = 'confirmRemoveFavorite';
        modalOpen.value = true;
    };

    const showRemoveFavoritesRemoveSeatModal = () => {
        if (isReserved.value) {
            activeModal.value = 'removeFavoritesRemoveSeat';
            modalOpen.value = true;
        } else {
            resetModal();
        }
    };

    const showRemoveFavoritesModal = () => {
        if (isFavorite.value) {
            activeModal.value = 'confirmRemoveFavorite';
            modalOpen.value = true;
        } else {
            resetModal();
        }
    };

    return {
        isFavorite,
        isFavoriteLoading,
        isSaveSeatLoading,
        activeModal,
        modalOpen,
        isReserved,
        toggleFavorite,
        handleSaveSeat,
        reserveSeat,
        unreserveSeat,
        resetModal,
        doubleBookedSession,
        findDoubleBookedSession,
        currentModalComponent,
        showRemoveFavoriteModal,
        showConfirmRemoveFavoritesModal,
        showRemoveFavoritesRemoveSeatModal,
        showRemoveFavoritesModal,
    };
}
